import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Typewriter hook ──────────────────────────────────────────────────────── */
function useTypewriter(text, speed = 38, startDelay = 600) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    const startTimer = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);
    return () => clearTimeout(startTimer);
  }, [text, speed, startDelay]);

  return { displayed, done };
}

/* ─── ContactHero ──────────────────────────────────────────────────────────── */
const ContactHero = () => {
  const videoRef        = useRef(null);
  const prevXRef        = useRef(null);
  const targetTimeRef   = useRef(0);   // where the mouse wants to be
  const displayedRef    = useRef(0);   // lazily follows target (lerp buffer)
  const animFrameRef    = useRef(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedServices, setServices]         = useState([]);

  const { displayed, done } = useTypewriter("we'd love to\nhear from you!");

  const availableServices = ['Brand', 'Digital', 'Campaign', 'Other'];

  /* ── Video: pause+scrub on desktop, autoplay loop on mobile ── */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const isMobile = window.innerWidth < 1024;

    /* Mobile — autoplay loop, no scrubbing */
    if (isMobile) {
      video.loop = true;
      video.play().catch(() => {});
      return;
    }

    /* Desktop — event-driven seek chain for zero-jitter scrubbing.
       Only sends seek N+1 after seeked fires for seek N.
       GPU decoder self-paces: no queue, no stalls, no jitter. */
    video.loop = false;
    video.pause();

    const pauseOnLoad = () => {
      video.pause();
      targetTimeRef.current = video.currentTime;
      displayedRef.current  = video.currentTime;
    };
    video.addEventListener('loadedmetadata', pauseOnLoad);
    if (video.readyState >= 1) pauseOnLoad();

    const LERP       = 0.14;
    const SETTLE_EPS = 0.004;
    let   isSeeking  = false;

    const advance = () => {
      if (!video.duration || isNaN(video.duration)) return;
      const diff = targetTimeRef.current - displayedRef.current;
      if (Math.abs(diff) < SETTLE_EPS) { isSeeking = false; return; }

      isSeeking = true;
      const next = displayedRef.current + diff * LERP;
      displayedRef.current  = next;
      video.currentTime     = Math.max(0, Math.min(video.duration, next));
    };

    const handleSeeked = () => advance();
    video.addEventListener('seeked', handleSeeked);

    const onMouseMove = (e) => {
      const duration = video.duration;
      if (!duration || isNaN(duration)) return;

      const x = e.clientX;
      if (prevXRef.current === null) { prevXRef.current = x; return; }

      const delta = x - prevXRef.current;
      prevXRef.current = x;

      const step = Math.max(-0.25, Math.min(0.25,
        (delta / window.innerWidth) * 0.65 * duration
      ));
      targetTimeRef.current = Math.max(0, Math.min(duration,
        targetTimeRef.current + step
      ));
      if (!isSeeking) advance();
    };

    const onMouseLeave = () => { prevXRef.current = null; };

    window.addEventListener('mousemove',  onMouseMove,  { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);

    return () => {
      window.removeEventListener('mousemove',  onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
      video.removeEventListener('loadedmetadata', pauseOnLoad);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, []);

  const toggleService = (s) =>
    setServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  return (
    <div className="relative bg-white text-neutral-900 font-sans selection:bg-[#EAECE9] selection:text-[#1C2E1E] antialiased overflow-x-hidden flex flex-col lg:block lg:min-h-screen">

      {/* ══ Background Video ══════════════════════════════════════════════════ */}
      <div className="order-last lg:order-none relative lg:absolute lg:inset-0 lg:z-0 overflow-hidden pointer-events-none w-full aspect-square md:aspect-video lg:aspect-auto lg:h-full bg-neutral-50 lg:bg-transparent">
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          className="w-full h-full object-cover object-right lg:object-right-bottom"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260601_110537_3a579fa0-7bbc-4d94-9d25-0e816c7840f5.mp4"
        />
      </div>

      {/* ══ Navbar ════════════════════════════════════════════════════════════ */}
      <header className="fixed top-0 inset-x-0 z-10 px-5 sm:px-8 py-4 sm:py-5 flex flex-row justify-between items-center bg-transparent">

        {/* Logo */}
        <Link to="/" className="flex flex-row items-center gap-3">
          <span className="text-[21px] sm:text-[26px] tracking-tight text-black font-medium select-none">
            Mainframe&reg;
          </span>
          <span className="text-[25px] sm:text-[30px] text-black select-none tracking-[-0.02em] font-medium leading-none mb-1">
            &#10033;
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex flex-row items-center text-[23px] text-black font-normal">
          {['Labs', 'Studio', 'Openings', 'Shop'].map((item, i, arr) => (
            <React.Fragment key={item}>
              <Link to="#" className="hover:opacity-60 transition-opacity">{item}</Link>
              {i < arr.length - 1 && <span className="opacity-40">,&nbsp;</span>}
            </React.Fragment>
          ))}
        </nav>

        {/* Desktop CTA */}
        <Link to="#"
          className="hidden md:inline-block text-[23px] text-black underline underline-offset-2 hover:opacity-60 transition-opacity">
          Get in touch
        </Link>

        {/* Hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen(o => !o)}
          className="md:hidden flex flex-col justify-between w-6 h-[16px] z-[101] relative focus:outline-none"
          aria-label="Toggle menu"
        >
          <span className={`w-6 h-[2px] bg-black transition-all duration-300 transform ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`w-6 h-[2px] bg-black transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
          <span className={`w-6 h-[2px] bg-black transition-all duration-300 transform ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>
      </header>

      {/* Mobile overlay */}
      <div className={`md:hidden fixed inset-0 z-[9] bg-white/95 backdrop-blur-sm transition-opacity duration-300 flex flex-col items-center justify-center gap-8 ${
        isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        {['Labs', 'Studio', 'Openings', 'Shop'].map(item => (
          <Link key={item} to="#"
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-3xl text-black hover:opacity-60 transition-opacity font-medium">
            {item}
          </Link>
        ))}
        <Link to="#"
          onClick={() => setIsMobileMenuOpen(false)}
          className="text-3xl text-black underline underline-offset-4 hover:opacity-60 transition-opacity font-medium mt-4">
          Get in touch
        </Link>
      </div>

      {/* ══ Content ═══════════════════════════════════════════════════════════ */}
      <div className="relative z-10 flex flex-col order-first lg:order-none w-full bg-white lg:bg-transparent pb-8 lg:pb-0 lg:min-h-screen">
        <main id="spade-hero" className="w-full max-w-7xl mx-auto px-6 py-12 flex-1 flex flex-col justify-center pt-24 lg:pt-32">

          {/* Typewriter headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl lg:text-[76px] font-normal tracking-tight text-black leading-[1.08] mb-8 select-none w-full whitespace-pre-wrap">
              {displayed}
              {!done && (
                <span className="inline-block w-[2px] h-[1.1em] bg-black align-middle ml-[2px] animate-blink" />
              )}
            </h1>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="text-lg md:text-xl text-[#5A635A] leading-relaxed font-normal mb-14 max-w-2xl">
              Whether you have questions, feedback, <br />
              drop us a message and we'll get back to you as soon as possible.
            </p>
          </motion.div>

          {/* Service pills */}
          <div className="max-w-2xl">
            <h2 className="text-2xl font-medium tracking-tight mb-2 text-black">
              What sort of service?
            </h2>
            <p className="opacity-85 text-[#738273] mb-8">Select all that apply</p>

            <motion.div layout className="flex flex-wrap gap-3 mb-6">
              {availableServices.map(service => {
                const isActive = selectedServices.includes(service);
                return (
                  <motion.button
                    layout
                    key={service}
                    onClick={() => toggleService(service)}
                    whileTap={{ scale: 0.95 }}
                    className={`px-6 py-3 rounded-full text-base font-medium transition-all duration-200 flex flex-row items-center gap-1.5 ${
                      isActive
                        ? 'bg-[#1C2E1E] text-white shadow-md shadow-emerald-950/5 transform'
                        : 'bg-white text-[#1C2E1E] border border-[#F1F3F1] hover:bg-[#F1F3F1]/55'
                    }`}
                  >
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.span
                          initial={{ scale: 0, width: 0, opacity: 0 }}
                          animate={{ scale: 1, width: 'auto', opacity: 1 }}
                          exit={{ scale: 0, width: 0, opacity: 0 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          className="flex items-center justify-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {service}
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Feedback status banner */}
            <div className="min-h-[50px]">
              <AnimatePresence mode="wait">
                {selectedServices.length === 0 ? (
                  <motion.p
                    key="empty"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 0.5, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="italic text-xs text-[#1C2E1E]"
                  >
                    Please click to select services above.
                  </motion.p>
                ) : (
                  <motion.div
                    key="active"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-row items-center justify-between bg-[#FAFBF9] border border-[#F1F3F1] rounded-2xl px-5 py-4">
                      <span className="text-[#1C2E1E] text-sm font-medium">
                        Ready to inquire about:{' '}
                        <span className="font-semibold">{selectedServices.join(', ')}</span>
                      </span>
                      <button className="flex items-center gap-1.5 text-[#4D6D47] font-semibold uppercase text-xs tracking-wider hover:opacity-80 transition-opacity">
                        Let's Go
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

        </main>
      </div>

    </div>
  );
};

export default ContactHero;
